/**
 * Typed functions for every `/api/deals` endpoint.
 *
 * Each function is a thin wrapper around `request<T>` that enforces
 * the request body shape and the response envelope. BigInt inputs are
 * accepted directly; the client walker will stringify them before JSON
 * encoding.
 *
 * Source of truth for paths + auth: src/api/routes/deals.ts
 */

import { apiGet, apiPost } from './client';
import type {
  CreateDealBody,
  CreateExtensionBody,
  CreateInviteBody,
  CreateInviteResponse,
  DecideExtensionBody,
  DealResponse,
  DealListResponse,
  DisputeResponse,
  ExtensionResponse,
  GetInviteResponse,
  ListDealsQuery,
  ListExtensionsResponse,
  ConfirmFundingBody,
  MarkDeliveredBody,
  CancelBody,
  DealContractInfo,
  OpenDisputeBody,
  OpenDisputeResponse,
} from './types';

/** POST /deals - create a new escrow deal. */
export async function createDeal(body: CreateDealBody): Promise<DealResponse> {
  return apiPost<DealResponse>('/deals', body);
}

/** GET /deals - list deals for the authenticated user. */
export async function listDeals(query: ListDealsQuery = {}): Promise<DealListResponse> {
  // Construct a flat query object so TypeScript can narrow the record to
  // the exact `QueryInput` shape expected by the client.
  return apiGet<DealListResponse>('/deals', {
    query: {
      status: query.status,
      role: query.role,
      limit: query.limit,
    },
  });
}

/** GET /deals/:shortId - fetch a single deal. */
export async function getDeal(shortId: string): Promise<DealResponse> {
  return apiGet<DealResponse>(`/deals/${encodeURIComponent(shortId)}`);
}

/**
 * GET /deals/:shortId/contract - minimal contract info for TonConnect
 * `sendTransaction`. Raises when the contract has not been deployed yet.
 */
export async function getDealContractInfo(shortId: string): Promise<DealContractInfo> {
  return apiGet<DealContractInfo>(`/deals/${encodeURIComponent(shortId)}/contract`);
}

/** POST /deals/:shortId/fund - buyer confirms they funded the contract. */
export async function confirmFunding(
  shortId: string,
  body: ConfirmFundingBody,
): Promise<DealResponse> {
  return apiPost<DealResponse>(`/deals/${encodeURIComponent(shortId)}/fund`, body);
}

/**
 * POST /deals/:shortId/check-funding — kick the backend's funding scan.
 *
 * Called by the Mini App right after Tonkeeper closes so the backend
 * doesn't have to wait for the next periodic scan tick (10s) before
 * detecting the on-chain deposit. Either party may call it; the worker
 * is idempotent. Best-effort: failures are non-fatal — the periodic
 * scan will catch the funding eventually.
 */
export async function kickFundingCheck(
  shortId: string,
): Promise<{ queued: boolean; status: string }> {
  return apiPost<{ queued: boolean; status: string }>(
    `/deals/${encodeURIComponent(shortId)}/check-funding`,
    {},
  );
}

/** POST /deals/:shortId/deliver - seller marks delivery complete. */
export async function markDelivered(
  shortId: string,
  body: MarkDeliveredBody = {},
): Promise<DealResponse> {
  return apiPost<DealResponse>(`/deals/${encodeURIComponent(shortId)}/deliver`, body);
}

/** POST /deals/:shortId/release - buyer releases escrow to seller. */
export async function releaseDeal(shortId: string): Promise<DealResponse> {
  return apiPost<DealResponse>(`/deals/${encodeURIComponent(shortId)}/release`, {});
}

/** POST /deals/:shortId/cancel - either party cancels before delivery. */
export async function cancelDeal(
  shortId: string,
  body: CancelBody = {},
): Promise<DealResponse> {
  return apiPost<DealResponse>(`/deals/${encodeURIComponent(shortId)}/cancel`, body);
}

/** POST /deals/:shortId/dispute - open a dispute on an active/delivered deal. */
export async function openDispute(
  shortId: string,
  body: OpenDisputeBody,
): Promise<OpenDisputeResponse> {
  return apiPost<OpenDisputeResponse>(
    `/deals/${encodeURIComponent(shortId)}/dispute`,
    body,
  );
}

/**
 * GET /deals/:shortId/dispute - fetch the dispute attached to a deal.
 * Returns 404 (caught as ApiError) when none exists.
 */
export async function getDisputeForDeal(shortId: string): Promise<DisputeResponse> {
  return apiGet<DisputeResponse>(`/deals/${encodeURIComponent(shortId)}/dispute`);
}

// ─── Pending invite (share-and-accept flow) ────────────────

/**
 * POST /deals/invite - buyer creates a new pending invite. No seller
 * required; the token + share link returned are used to invite the
 * seller via Telegram native share sheet.
 */
export async function createInvite(
  body: CreateInviteBody,
): Promise<CreateInviteResponse> {
  return apiPost<CreateInviteResponse>('/deals/invite', body);
}

/**
 * GET /deals/invite/:token - fetch a pending invite for preview. Used
 * by the InvitePage when the seller lands via the share deep link.
 */
export async function getInvite(token: string): Promise<GetInviteResponse> {
  return apiGet<GetInviteResponse>(`/deals/invite/${encodeURIComponent(token)}`);
}

/**
 * POST /deals/invite/:token/accept - seller accepts. Backend creates the
 * real deal via dealService.create, deploys the child escrow, and
 * returns the new deal row.
 */
export async function acceptInvite(token: string): Promise<DealResponse> {
  return apiPost<DealResponse>(
    `/deals/invite/${encodeURIComponent(token)}/accept`,
    {},
  );
}

// ─── Deadline extensions ───────────────────────────────────

/**
 * POST /deals/:shortId/extensions — create a deadline extension.
 *
 * Branches on caller role (handled server-side):
 *   - Buyer → applied immediately, response carries `status: 'applied'`
 *   - Seller → pending, response carries `status: 'pending'`
 */
export async function createExtension(
  shortId: string,
  body: CreateExtensionBody,
): Promise<ExtensionResponse> {
  return apiPost<ExtensionResponse>(
    `/deals/${encodeURIComponent(shortId)}/extensions`,
    body,
  );
}

/** GET /deals/:shortId/extensions — list all extensions for a deal. */
export async function listExtensions(
  shortId: string,
): Promise<ListExtensionsResponse> {
  return apiGet<ListExtensionsResponse>(
    `/deals/${encodeURIComponent(shortId)}/extensions`,
  );
}

/** POST /deals/:shortId/extensions/:id/approve — buyer accepts a pending request. */
export async function approveExtension(
  shortId: string,
  extensionId: string,
  body: DecideExtensionBody = {},
): Promise<ExtensionResponse> {
  return apiPost<ExtensionResponse>(
    `/deals/${encodeURIComponent(shortId)}/extensions/${encodeURIComponent(extensionId)}/approve`,
    body,
  );
}

/** POST /deals/:shortId/extensions/:id/reject — buyer rejects a pending request. */
export async function rejectExtension(
  shortId: string,
  extensionId: string,
  body: DecideExtensionBody = {},
): Promise<ExtensionResponse> {
  return apiPost<ExtensionResponse>(
    `/deals/${encodeURIComponent(shortId)}/extensions/${encodeURIComponent(extensionId)}/reject`,
    body,
  );
}
